<template>
  <div>
    <LoadingSpinner
      class="mb-3"
      v-if="landownerReportsLoading"
      :message="`Loading Landowner Reports`" />
    <template v-else-if="landownerReports.length">
      <!-- Add Start Landowner Report Modal -->
      <b-modal hide-footer ref="startReportConfirmModal" id="startReportConfirmModal"  centered hide-header-close no-close-on-backdrop
        no-stacking  size="md" title="Start Landowner Report">
        <p>
          You are about to start the landowner report. If you continue, you will be the only party (landowner or designated agent) who can complete this report.
          Will you complete the landowner report?
        </p>
        <p>
          If you select "No" then you must contact the other party (landowner or designated agent) on the FON to complete the report. This report must be completed by one of you.
        </p>
        <div class="d-flex flex-column  full-width">
          <b-button
            pill
            variant="primary"
            class="mb-3 mb-lg-4"
            @click="create()"
            :disabled="isButtonDisabled"
            aria-label="Confirm starting this Landowner Report"
          >
            Yes
          </b-button>
          <b-button
            v-if="this.notification.designatedAgentUser == null"
            pill
            variant="danger"
            :disabled="isButtonDisabled"
            @click="closeConfirmModal()"
            aria-label="Cancel starting this Landowner Report"
          >
            No
          </b-button>
          <b-button
            v-else-if="this.notification.designatedAgentUser != null"
            pill
            variant="danger"
            @click="openNotStartConfirmModal()"
            aria-label="Open not started confirmation modal for this Landowner Report"
          >
            No
          </b-button>
        </div>
        <div slot="modal-footer" class="d-flex full-width">
        </div>
      </b-modal>
      <!-- Add Start Landowner Report Modal -->
      <b-modal hide-footer ref="notStartReportConfirmModal" id="notStartReportConfirmModal"  centered hide-header-close no-close-on-backdrop
        no-stacking  size="md" title="">
        <p>
          {{getLandowerOrDesignatedAgentNotification}}
        </p>
        <div class="d-flex flex-column  full-width">
          <b-button
            pill
            variant="primary"
            class="mb-3 mb-lg-4"
            @click="closeNotStartConfirmModal()"
            aria-label="Confirm not starting this Landowner Report"
          >
            OK
          </b-button>
        </div>
        <div slot="modal-footer" class="d-flex full-width">
        </div>
      </b-modal>
      <b-modal hide-footer ref="startPaperReportConfirmModal" id="startPaperReportConfirmModal"  centered hide-header-close no-close-on-backdrop
        no-stacking  size="md" title="Complete Landowner Report">
        <p>
          By clicking the "Complete Landowner Report" button below, you certify that you, as MFS Admin, will be entering the data for this landowner report.
        </p>
        <div class="d-flex flex-column  full-width">
          <b-button
            pill
            variant="primary"
            class="mb-3 mb-lg-4"
            @click="create()"
            :disabled="isButtonDisabled"
            aria-label="Confirm completing this Landowner Report"
          >
            Complete Report
          </b-button>
          <b-button
            pill
            variant="danger"
            :disabled="isButtonDisabled"
            @click="closePaperConfirmModal()"
            aria-label="Cancel completing this Landowner Report"
          >
            Cancel
          </b-button>
        </div>
        <div slot="modal-footer" class="d-flex full-width">
        </div>
      </b-modal>
      <b-list-group class="mb-3">
        <b-list-group-item
          v-for="report in landownerReports"
          :key="report.reportingYear"
          class="row no-gutters d-flex justify-content-between align-items-center border-dark"
        >
          <b-col cols="12" md="7" class="mb-3 mb-md-0">
            {{report.reportingYear}} Report: {{getReportStatusText(report)}}
            {{report.pastDue && !reportComplete(report) && !report.notRequired ? '| PAST DUE' : ''}}
          </b-col>
          <b-col cols="12" md="5" class="lo-report-due">
            <div v-if="report.notRequired">
              A previous year's report indicated harvesting as complete.
            </div>
            <div v-else-if="report.previousYearIncomplete">
              Previous report must be completed first.
            </div>
            <div v-else-if="report.canStart"
              class="w-100 w-md-auto">
              <b-button
                class="w-100 w-md-auto float-right"
                v-if="reportComplete(report) && userCanReport && !mfsAdmin"
                pill
                @click="viewReport(report.id)"
                aria-label="View this Landowner Report"
              >
                View
              </b-button>
              <div v-else-if="reportComplete(report) && (mfsUser || mfsAdmin)">
                <b-button
                  class="w-100 w-md-auto float-right"
                  pill
                  @click="$bvModal.show(`report-view-confirm-${report.id}`)"
                  aria-label="View this Landowner Report"
                >
                  View
                </b-button>
                <b-modal
                  :ref="`report-view-confirm-${report.id}`"
                  :id="`report-view-confirm-${report.id}`"
                  title="View Landowner Report"
                  footer-class="text-center"
                  centered
                  hide-header-close
                  size="lg"
                >
                  <p>
                    You are about to view a confidential landowner report (12 M.R.S. §8885 (4)).
                    By continuing to view the report, you are confirming that you have a valid business
                    reason to view it. As a MFS staff member, it is your responsibility to maintain the
                    confidentiality of this information. You must not share any information in this report
                    with anyone. The unauthorized viewing or sharing of confidential information may result
                    in disciplinary action and/or have legal consequences. If a non-MFS party requests
                    information from a landowner report, please direct them to contact
                    Greg Lord (gregory.r.lord@maine.gov or 207-287-4950).
                    <strong>Clicking “View Landowner Report" constitutes acceptance of these terms</strong>.
                  </p>
                  <div slot="modal-footer" class="d-flex full-width">
                    <b-button
                      pill
                      variant="danger"
                      class="mr-3"
                      @click="$bvModal.hide(`report-view-confirm-${report.id}`)"
                      aria-label="Cancel viewing this Landowner Report"
                    >
                      Do not view Landowner Report
                    </b-button>
                    <b-button
                      pill
                      variant="primary"
                      @click="viewReport(report.id)"
                      aria-label="Confirm viewing this Landowner Report"
                    >
                      View Landowner Report
                    </b-button>
                  </div>
                </b-modal>
              </div>
              <b-button
                class="w-100 w-md-auto float-right report_option_button"
                v-else-if="draftLorUser(report)"
                pill
                @click="goToReport(report.id)"
                aria-label="Continue working on this Landowner Report"
              >
                Continue Working
              </b-button>
              <b-button
                class="w-100 w-md-auto float-right"
                v-else-if="userCanReport && !report.alreadyStarted && !reportWorkingByMfsAdmin(report) && !mfsAdmin"
                pill
                :disabled="starting"
                @click="openConfirmModal(report.reportingYear)"
                aria-label="Start a new Landowner Report"
              >
                {{startText}}
              </b-button>
              <b-button
                class="w-100 w-md-auto float-right"
                v-else-if="mfsAdmin && !report.alreadyStarted"
                pill
                :disabled="starting"
                @click="openPaperConfirmModal(report.reportingYear)"
                aria-label="Complete this Landowner Report"
              >
                Complete Report
              </b-button>
              <div v-if="mfsAdmin && report.alreadyStarted && !reportWorkingByMfsAdmin(report)">
                <div class="w-100 w-md-auto ">
                  <DeleteReportModal
                    class="mb-2 mr-3"
                    :modalDisabled="false"
                    :reportId=report.id
                  />
                  <b-button
                    class="w-100 w-md-auto float-right report_option_button"
                    pill
                    :disabled="starting"
                    @click="goToReport(report.id)"
                    aria-label="View this Landowner Report"
                  >
                    View Report
                </b-button>
                </div>
              </div>
              <div
                v-if="userCanReport && reportWorking(report) && !reportWorkingByMfsAdmin(report) && !mfsAdmin"
                class="w-100 w-md-auto mx-2">
                <WithdrawReportModal
                  class="mb-2 mr-3"
                  :modalDisabled="false"
                  :reportId=report.id
                />
              </div>
            </div>
            <div v-else-if="!reportComplete(report)">
              Report due between {{report.startDate | dateString}}
              and {{report.endDate | dateString}}
            </div>
          </b-col>
        </b-list-group-item>
      </b-list-group>
      <em class="text-primary"><strong>What is the Landowner Report?</strong></em>
      <p>
        Forest landowners who file a "Forest Operations Notification" with at least one harvest
        activity are required to complete a Landowner Report <strong>each year</strong> between the
        operation start and end dates, <strong>whether or not a harvest occurred</strong> until a
        Landowner Report is submitted indicating that the harvest is complete. Our records indicate
        that this Notification requires landowner reporting, which must be done by either the
        <strong>landowner</strong> or the <strong>designated agent</strong> listed on the
        Notification.
      </p>
      <p>THIS INFORMATION IS <strong><u>CONFIDENTIAL</u></strong>.</p>
      <em class="text-primary"><strong>How is the Landowner Report used?</strong></em>
      <p>
        As mandated by the legislature, the Maine Forest Service uses information from this report
        to:
        <ul class="summary-list mt-3">
          <li>Produce the
            <a
              :href="links.silvicultural"
              target="_blank"
              rel="noopener noreferrer"
            >
            Silvicultural Activities Report</a> and the annual
            <a
              :href="links.stumpagePriceReport"
              target="_blank"
              rel="noopener noreferrer"
            >
            Stumpage Price Report</a>.</li>
          <li>Determine timberland valuations in the Tree Growth Tax Program.</li>
          <li>Monitor trends in forest management activities.</li>
        </ul>
      </p>
      <em class="text-primary"><strong>Who needs to fill this out?</strong></em>
      <p>
        It is important for the landowner (or designated agent) to complete this report accurately.
      </p>
      <em class="text-primary"><strong>When is this report due?</strong></em>
      <p>
        Please complete each Landowner Report <u>no later than</u>&nbsp;<strong>January 31</strong>
        that follows the reporting year.
      </p>
    </template>
    <span v-else>
      Landowner Reports not required for this Notification, because no harvest activity was
      indicated.
    </span>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import LookupModules from '@/store/moduleConfig';
import WithdrawReportModal from '@/components/notification/summary/WithdrawReportModal.vue';
import DeleteReportModal from '@/components/notification/summary/DeleteReportModal.vue';

export default {
  name: 'LandownerReports',
  components: {
    LoadingSpinner,
    WithdrawReportModal,
    DeleteReportModal,
  },
  data() {
    return {
      starting: false,
      isButtonDisabled: false,
      selectedYear: 0,
    };
  },
  created() {
    this.$store.dispatch('Notification/fetchLandownerReports');
    this.$store.dispatch(`${LookupModules.LANDOWNER_REPORT_STATUSES.namespace}/fetchOptions`);
  },
  computed: {
    ...mapState('User', {
      user: state => state.user,
    }),
    ...mapState('Configuration', {
      links: state => state.links,
    }),
    ...mapState('Notification', {
      notification: state => state.notification,
      landownerReports: state => state.landownerReports,
      landownerReportsLoading: state => state.landownerReportsLoading,
    }),
    ...mapGetters('User', ['mfsUser', 'mfsAdmin']),
    ...mapGetters('Notification', ['closed']),
    ...mapGetters(LookupModules.LANDOWNER_REPORT_STATUSES.namespace, {
      getStatusName: 'getName',
    }),
    userCanReport() {
      return this.userIsLandowner || this.userIsDesignatedAgent || this.userIsMfsAdmin;
    },
    userIsLandowner() {
      if (this.notification.landownerUser) {
        return this.notification.landownerUser.id === this.user.id;
      }
      return false;
    },
    userIsDesignatedAgent() {
      if (this.notification.designatedAgentUser) {
        return this.notification.designatedAgentUser.id === this.user.id;
      }
      return false;
    },
    userIsMfsAdmin() {
      if (this.mfsAdmin) {
        return true;
      }
      return false;
    },
    startText() {
      return this.starting ? 'Start...' : 'Start';
    },
    getLandowerOrDesignatedAgentNotification() {
      let contactFirstName;
      let contactLastName;
      let contactFullName;
      let contactEmail;
      let contactUserInfo;
      let notificationText;
      let contactUser;
      if (this.userIsLandowner) {
        contactUser = this.notification.designatedAgentUser;
      } else {
        contactUser = this.notification.landownerUser;
      }
      if (contactUser != null) {
        contactFirstName = contactUser.firstName;
        contactLastName = contactUser.lastName;
        contactEmail = contactUser.email;
        contactFullName = `${contactFirstName.replace(/^./, contactFirstName[0].toUpperCase())} ${contactLastName.replace(/^./, contactLastName[0].toUpperCase())}`;
        contactUserInfo = `${contactFullName} (${contactEmail}) `;
        notificationText = 'Since you will not be completing this landowner report, please contact ';
        notificationText += `${contactUserInfo} to inform them to complete this landowner report.`;
      }
      return notificationText;
    },
  },
  methods: {
    updateReportList() {
      this.$store.dispatch('Notification/fetchLandownerReports');
    },
    openConfirmModal(year) {
      this.selectedYear = year;
      this.$refs.startReportConfirmModal.show();
    },
    openNotStartConfirmModal() {
      this.$refs.startReportConfirmModal.hide();
      this.$refs.notStartReportConfirmModal.show();
    },
    closeNotStartConfirmModal() {
      this.$refs.notStartReportConfirmModal.hide();
    },
    closeConfirmModal() {
      this.$refs.startReportConfirmModal.hide();
    },
    openPaperConfirmModal(year) {
      this.selectedYear = year;
      this.$refs.startPaperReportConfirmModal.show();
    },
    closePaperConfirmModal() {
      this.$refs.startPaperReportConfirmModal.hide();
    },
    create() {
      this.isButtonDisabled = true;
      this.starting = true;
      this.$store.dispatch(
        'LandownerReport/create',
        {
          data: { reportingYear: this.selectedYear, notificationId: this.notification.id },
          submittedCallback: this.goToReport,
        },
      );
    },
    getReportStatusText(landownerReport) {
      if (landownerReport.notRequired) return 'Not Required';
      return this.getStatusName(landownerReport.statusId) || 'Not Started';
    },
    draftLorUser(landownerReport) {
      return landownerReport.createdBy === this.user.id;
    },
    reportComplete(landownerReport) {
      return (landownerReport.statusId === 2 || landownerReport.statusId === 4 || landownerReport.statusId === 5);
    },
    reportWorking(landownerReport) {
      return landownerReport.statusId === 1;
    },
    reportWorkingByMfsAdmin(landownerReport) {
      return landownerReport.statusId === 3;
    },
    goToReport(id) {
      this.$router.push({ name: 'landownerReport', params: { id } });
    },
    viewReport(id) {
      this.$store.dispatch(
        'LandownerReport/viewed',
        {
          id,
          submittedCallback: this.goToReport,
        },
      );
    },
  },
};
</script>
